import { ColumnDef } from "@tanstack/react-table";
import { BiDotsHorizontalRounded } from "react-icons/bi";

// Helper functions (you'll need to import these from your actual helpers)
const enumToStringConverter = (value: string) => {
	if (!value) return "";
	return value
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

const formatDate = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleDateString();
};

const moneyFormat = (amount: number) => {
	if (!amount) return "0.00";
	return amount.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

const getOrganizationStatusColor = (status: string) => {
	switch (status) {
		case "ACTIVE":
			return "#4CAF50";
		case "TERMINATED":
			return "#808080";
		case "DISABLED":
			return "#F44336";
		case "ELAPSED_CONTRACT":
			return "#FFC107";
		case "ARCHIVED":
			return "#2196F3";
		default:
			return "#BF21F3";
	}
};

export const tanstackEmployeeColumns: ColumnDef<any>[] = [
	{
		id: "employeeName",
		accessorFn: (row) => `${row.firstName} ${row.lastName}`,
		header: "Employee Name",
		size: 260,
		minSize: 200,
		maxSize: 400,
		cell: ({ row }) => (
			<div className="flex items-center">
				<p className="text-sm truncate">
					{row.original.firstName} {row.original.lastName}
				</p>
			</div>
		),
	},
	{
		accessorKey: "gender",
		header: "Gender",
		size: 150,
		minSize: 100,
		maxSize: 200,
		cell: ({ getValue }) => enumToStringConverter(getValue() as string),
	},
	{
		accessorKey: "title",
		header: "Title",
		size: 180,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "dateOfBirth",
		header: "Date Of Birth",
		size: 170,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "employeeNumber",
		header: "Employee Number",
		size: 200,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "nationality",
		header: "Nationality",
		size: 180,
		minSize: 150,
		maxSize: 250,
	},
	{
		accessorKey: "email",
		header: "Corporate Email Address",
		size: 265,
		minSize: 200,
		maxSize: 400,
	},
	{
		accessorKey: "privateEmail",
		header: "Private Email Address",
		size: 265,
		minSize: 200,
		maxSize: 400,
	},
	{
		accessorKey: "status",
		header: "Employee Status",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const status = getValue() as string;
			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: getOrganizationStatusColor(status) }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: getOrganizationStatusColor(status),
							opacity: 0.15,
						}}
					/>
					<p>{enumToStringConverter(status)}</p>
				</div>
			);
		},
	},
	{
		id: "netSalary",
		accessorFn: (row) => {
			const amount = row?.employeePayInfo?.basicPay;
			const currency = row?.employeePayInfo?.companyCurrency?.currency?.code;
			return { amount, currency };
		},
		header: "Net Salary",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => {
			const { amount, currency } = getValue() as any;
			return `${currency || ""} ${moneyFormat(amount) || ""}`;
		},
	},
	{
		// Explicit id avoids falling back to the accessorKey ("msisdn") which
		// ManageTableColumns would display verbatim as the column label.
		id: "phoneNumber",
		accessorKey: "msisdn",
		header: "Phone Number",
		size: 220,
		minSize: 180,
		maxSize: 300,
	},
	{
		// Same as above — "alternateMsisdn" is an internal field name, not a
		// user-facing label. The explicit id keeps the visibility key readable.
		id: "alternatePhoneNumber",
		accessorKey: "alternateMsisdn",
		header: "Alternate Phone Number",
		size: 220,
		minSize: 180,
		maxSize: 300,
	},
	{
		accessorKey: "ssn",
		header: "SSN",
		size: 220,
		minSize: 150,
		maxSize: 300,
	},
	{
		accessorKey: "address",
		header: "Address",
		size: 300,
		minSize: 200,
		maxSize: 500,
	},
	{
		accessorKey: "digitalAddress",
		header: "Digital Address",
		size: 300,
		minSize: 200,
		maxSize: 500,
	},
	{
		accessorKey: "employmentDate",
		header: "Employment Date",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "terminationDate",
		header: "Termination Date",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => {
			const value = getValue();
			return value ? formatDate({ value }) : "--";
		},
	},
	{
		accessorKey: "unionMember",
		header: "Union Member",
		size: 180,
		minSize: 120,
		maxSize: 250,
		cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
	},
	{
		accessorKey: "reemployed",
		header: "Reemployed",
		size: 180,
		minSize: 120,
		maxSize: 250,
		cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
	},
	{
		accessorKey: "resident",
		header: "Resident",
		size: 180,
		minSize: 120,
		maxSize: 250,
		cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
	},
	{
		accessorKey: "hr",
		header: "HR Personnel",
		size: 180,
		minSize: 120,
		maxSize: 250,
		cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "modifiedAt",
		header: "Date Modified",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		maxSize: 150,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

// Column visibility keys must match column ids. The phone number columns use
// explicit ids ("phoneNumber", "alternatePhoneNumber") so the Manage Columns
// panel shows human-readable labels instead of the raw field names.
export const defaultColumnVisibility = {
	employeeName: true,
	gender: true,
	title: false,
	dateOfBirth: false,
	employeeNumber: true,
	nationality: true,
	email: true,
	privateEmail: false,
	status: true,
	netSalary: true,
	phoneNumber: true,
	alternatePhoneNumber: false,
	ssn: false,
	address: false,
	digitalAddress: false,
	employmentDate: false,
	terminationDate: false,
	unionMember: false,
	reemployed: false,
	resident: false,
	hr: false,
	createdAt: true,
	modifiedAt: true,
	actions: true,
};
